import { CalendarHeader } from "@/features/dash/home/components/calendar-header";
import { CustomTimeGridEvent, TimeGridEventData } from "@/features/dash/home/components/custom-time-grid-event";
import { WeekGridDate } from "@/features/dash/home/components/week-grid-date";
import { WeekGridHour } from "@/features/dash/home/components/week-grid-hour";
import { AttendanceSheet } from "@/features/dash/home/components/attendance-sheet";
import { formatLocalDate, useWeekAnchor } from "@/hooks/useWeekAnchor";
import { api } from "@/lib/api";
import { createViewWeek } from "@schedule-x/calendar";
import { createCalendarControlsPlugin } from "@schedule-x/calendar-controls";
import { createCurrentTimePlugin } from "@schedule-x/current-time";
import { createEventsServicePlugin } from "@schedule-x/events-service";
import { ScheduleXCalendar, useCalendarApp } from "@schedule-x/react";
import "@schedule-x/theme-default/dist/index.css";
import { useEffect, useState, useCallback, useMemo } from "react";
import "temporal-polyfill/global";
import { PlanningCountsProvider } from "@/hooks/usePlanningCounts";
import { usePlanningCountsSource } from "@/hooks/usePlanningCountsCore";

// Types adaptés pour correspondre aux types du backend
type PlanningPeriod = {
  id: string;
  period: "matin" | "midi" | "apres-midi" | "journee";
  startTime: string; // Format "HH:MM"
  endTime: string; // Format "HH:MM"
  maxChildren: number;
  dayOfWeek: number; // 1 = Monday, 7 = Sunday
  date: string; // Format "YYYY-MM-DD"
  currentChildren: number;
};

type PlanningReservation = {
  id: string;
  childId: string;
  childName: string;
  periodId: string;
  date: string; // Format "YYYY-MM-DD"
};

type PeriodData = {
  periodTitle: string;
  periodDate: string;
  periodTime: string;
  periodId: string; // ID de la période
  date: string; // Date au format YYYY-MM-DD
  targetGroupId?: string;
};

// Extension locale pour enrichir les events
interface PlanningEventExtra {
  periodId: string;
  date: string; // YYYY-MM-DD
}

// (interface supprimée car non indispensable, on utilisera un type inline)

// Fonction pour obtenir le libellé français d'une période
const getPeriodLabel = (period: "matin" | "midi" | "apres-midi" | "journee"): string => {
  switch (period) {
    case "matin":
      return "Matinée";
    case "midi":
      return "Midi";
    case "apres-midi":
      return "Après-midi";
    case "journee":
      return "Journée";
  }
};

// Fonction pour obtenir la couleur d'une période
const getPeriodColor = (period: "matin" | "midi" | "apres-midi" | "journee"): string => {
  switch (period) {
    case "matin":
      return "#42B8EF"; // Blue
    case "midi":
      return "#77C57C"; // Green
    case "apres-midi":
      return "#F07062"; // Red
    case "journee":
      return "#9C88FF"; // Purple
  }
};

// Fonction pour calculer les bornes dynamiques du jour à partir des 7 jours déjà calculés
const calculateDayBoundaries = (periods: PlanningPeriod[], weekDays: Date[]) => {
  // Valeurs par défaut si aucune période n'est trouvée
  if (!periods || periods.length === 0) {
    // console.log("No periods found, using default day boundaries.");
    return {
      start: "08:00",
      end: "19:00",
    };
  }

  let earliestStart = "23:59";
  let latestEnd = "00:00";

  for (const d of weekDays) {
    const dayOfWeek = d.getDay() === 0 ? 7 : d.getDay();
    const dayPeriods = periods.filter((p) => p.dayOfWeek === dayOfWeek);

    for (const period of dayPeriods) {
      if (period.startTime < earliestStart) {
        earliestStart = period.startTime;
      }
      if (period.endTime > latestEnd) {
        latestEnd = period.endTime;
      }
    }
  }

  // Si aucune période trouvée, utiliser les valeurs par défaut
  if (earliestStart === "23:59" && latestEnd === "00:00") {
    return {
      start: "08:00",
      end: "19:00",
    };
  }

  // Ajouter une marge d'une heure avant et après
  const startHour = Math.max(0, parseInt(earliestStart.split(":")[0]));
  const endHour = Math.min(23, parseInt(latestEnd.split(":")[0]) + (parseInt(latestEnd.split(":")[1]) > 0 ? 1 : 0));

  return {
    start: `${startHour.toString().padStart(2, "0")}:00`,
    end: `${endHour.toString().padStart(2, "0")}:00`,
  };
};

// Ajout: utilitaire pour calculer la valeur de base dynamique selon la hauteur de l'écran
const calculateBasePixelsPerHour = (): number => {
  const screenHeight = window.innerHeight;
  // Formule: prendre environ 10% de la hauteur d'écran comme base
  // Avec un minimum de 40 et maximum de 100
  const base = Math.round(screenHeight * 0.1);
  return Math.max(40, Math.min(100, base));
};

// Mapping des niveaux de zoom vers les multiplicateurs
// 0 = 50%, 1 = 75%, 2 = 100%, 3 = 150%, 4 = 200%
const ZOOM_MULTIPLIERS = [0.5, 0.75, 1, 1.5, 2];

// Ajout: utilitaire pour calculer gridHeight en fonction des bornes horaires et du niveau de zoom
const computeGridHeight = (bounds: { start: string; end: string }, zoomLevel: number = 2): number => {
  const [sh, sm] = bounds.start.split(":").map(Number);
  const [eh, em] = bounds.end.split(":").map(Number);
  const start = sh + (sm || 0) / 60;
  const end = eh + (em || 0) / 60;
  const hours = Math.max(1, end - start);

  const basePixelsPerHour = calculateBasePixelsPerHour();
  const multiplier = ZOOM_MULTIPLIERS[zoomLevel] || 1;

  return Math.round(hours * basePixelsPerHour * multiplier);
};

// Fonction pour générer les événements du calendrier
const generateCalendarEvents = (periods: PlanningPeriod[], reservations: PlanningReservation[], weekDays: Date[]): TimeGridEventData[] => {
  const events: TimeGridEventData[] = [];
  // (supprimé) Ancienne logique liée au bouton d'appel

  // Vérification de sécurité
  if (!periods || !reservations) {
    return events;
  }

  // Données météo fictives pour la démonstration
  const weatherData = [
    { type: "sunny" as const, temperature: 22 },
    { type: "cloudy" as const, temperature: 18 },
    { type: "partly-cloudy" as const, temperature: 20 },
    { type: "rainy" as const, temperature: 15 },
    { type: "stormy" as const, temperature: 12 },
  ];

  for (let dayOffset = 0; dayOffset < weekDays.length; dayOffset++) {
    const currentDate = weekDays[dayOffset];
    const dateStr = formatLocalDate(currentDate);
    const dayOfWeek = currentDate.getDay() === 0 ? 7 : currentDate.getDay();

    // Trouver les périodes pour ce jour
    const dayPeriods = periods.filter((p) => p.dayOfWeek === dayOfWeek && p.date === dateStr);

    for (const period of dayPeriods) {
      // Filtrer les réservations pour cette période à cette date
      const periodReservations = reservations.filter((r) => r.periodId === period.id && r.date === dateStr);

      const title = `${getPeriodLabel(period.period)} (${period.currentChildren}/${period.maxChildren})`;

      // Sélectionner des données météo aléatoirement pour la démo
      const weather = weatherData[dayOffset % weatherData.length];

      const event: TimeGridEventData & PlanningEventExtra = {
        id: `${period.id}-${dateStr}`,
        title,
        start: Temporal.ZonedDateTime.from(`${dateStr}T${period.startTime}:00[Europe/Paris]`),
        end: Temporal.ZonedDateTime.from(`${dateStr}T${period.endTime}:00[Europe/Paris]`),
        color: getPeriodColor(period.period),
        description: `${periodReservations.map((r) => r.childName).join(", ")}`,
        // Données personnalisées pour notre composant
        period: period.period,
        currentChildren: period.currentChildren,
        maxChildren: period.maxChildren,
        weather,
        backgroundColor: getPeriodColor(period.period),
        // Champs supplémentaires pour simplifier le clic
        periodId: period.id,
        date: dateStr,
      };

      events.push(event);
    }
  }

  return events;
};

// Composant interne pour le calendrier qui sera recréé quand le zoom change
function PlanningCalendar({
  anchorDate,
  weekDays,
  dayBoundaries,
  gridHeight,
  planningData,
  handleOpenAttendance,
  isLoading,
}: {
  anchorDate: Date;
  weekDays: Date[];
  dayBoundaries: { start: string; end: string };
  gridHeight: number;
  planningData: { periods: PlanningPeriod[]; reservations: PlanningReservation[] } | undefined;
  handleOpenAttendance: (periodData: PeriodData) => void;
  isLoading: boolean;
}) {
  const eventsService = createEventsServicePlugin();
  const calendarControls = createCalendarControlsPlugin();

  const calendar = useCalendarApp({
    views: [createViewWeek()],
    events: [], // Initialement vide, sera mis à jour par useEffect
    plugins: [eventsService, calendarControls, createCurrentTimePlugin()],
    selectedDate: Temporal.PlainDate.from(formatLocalDate(anchorDate)),
    locale: "fr-FR",
    timezone: "Europe/Paris",
    firstDayOfWeek: 1, // Lundi
    dayBoundaries,
    weekOptions: {
      // Number of hours to display in the grid * 100
      gridHeight,
    },
    callbacks: {},
  });

  // Mettre à jour les événements quand les données de l'API arrivent
  useEffect(() => {
    if (planningData) {
      const newEvents = generateCalendarEvents(planningData.periods, planningData.reservations, weekDays);

      // Optimisation : ne mettre à jour que si les événements ont changé
      const currentEvents = eventsService.getAll();
      const newEventIds = new Set(newEvents.map((e) => e.id));

      // Supprimer les événements qui ne sont plus nécessaires
      currentEvents.forEach((event) => {
        if (!newEventIds.has(event.id)) {
          eventsService.remove(event.id);
        }
      });

      // Ajouter les nouveaux événements ou mettre à jour ceux qui existent déjà si des champs ont changé
      newEvents.forEach((event) => {
        const existing = currentEvents.find((e) => e.id === event.id) as (TimeGridEventData & Partial<PlanningEventExtra>) | undefined;
        if (!existing) {
          // Nouvel event
          eventsService.add(event);
          return;
        }

        // Déterminer si un update est nécessaire (compte, titres, horaires, couleur, description)
        const needsUpdate =
          existing.title !== event.title ||
          existing.currentChildren !== event.currentChildren ||
          existing.maxChildren !== event.maxChildren ||
          String(existing.start) !== String(event.start) ||
          String(existing.end) !== String(event.end) ||
          existing.color !== event.color ||
          existing.description !== event.description;

        if (needsUpdate) {
          eventsService.update({ ...existing, ...event });
        }
      });

      // Les compteurs optimistes sont désormais gérés via le hook et appliqués dans l'UI via le contexte
    }
  }, [planningData, weekDays, eventsService]);

  // Mettre à jour la date sélectionnée quand la semaine change
  useEffect(() => {
    calendarControls.setDate(Temporal.PlainDate.from(formatLocalDate(anchorDate)));
  }, [anchorDate, calendarControls]);

  return (
    <div
      className={`sx-react-calendar-wrapper relative ${isLoading ? "planning-loading-blur" : ""} ${!isLoading && (!planningData?.periods || planningData.periods.length === 0) ? "planning-overlay-blur" : ""}`}
    >
      <ScheduleXCalendar
        calendarApp={calendar}
        customComponents={{
          headerContent: () => <div></div>, // Header vide pour utiliser nos contrôles
          weekGridDate: (props: { date: string }) => <WeekGridDate date={props.date} />,
          weekGridHour: (props: { hour: number; gridStep: { hour: number; minute: number } }) => <WeekGridHour hour={props.hour} gridStep={props.gridStep} />,
          timeGridEvent: (props: { calendarEvent: TimeGridEventData & Partial<PlanningEventExtra> }) => (
            <CustomTimeGridEvent
              calendarEvent={props.calendarEvent}
              onSelectPeriod={(evt) => {
                // Utiliser de préférence les champs enrichis
                let periodId = (evt as TimeGridEventData & Partial<PlanningEventExtra>).periodId;
                let date = (evt as TimeGridEventData & Partial<PlanningEventExtra>).date;
                if (!periodId || !date) {
                  const idStr = String(evt.id);
                  const lastDashIndex = idStr.lastIndexOf("-");
                  if (lastDashIndex === -1) return;
                  periodId = idStr.slice(0, lastDashIndex);
                  date = idStr.slice(lastDashIndex + 1);
                }
                if (!date || !/^\d{4}-\d{2}-\d{2}$/.test(date)) return;
                const startZdt = evt.start as Temporal.ZonedDateTime;
                const endZdt = evt.end as Temporal.ZonedDateTime;
                handleOpenAttendance({
                  periodTitle: evt.title?.replace(/\s*\(.*$/, "") || "Période",
                  periodDate: new Date(date).toLocaleDateString("fr-FR", {
                    weekday: "long",
                    day: "numeric",
                    month: "long",
                  }),
                  periodTime: `${startZdt.toPlainTime().hour.toString().padStart(2, "0")}:${startZdt
                    .toPlainTime()
                    .minute.toString()
                    .padStart(2, "0")} - ${endZdt.toPlainTime().hour.toString().padStart(2, "0")}:${endZdt.toPlainTime().minute.toString().padStart(2, "0")}`,
                  periodId: periodId!,
                  date,
                  targetGroupId: undefined,
                });
              }}
            />
          ), // Notre composant personnalisé avec clic
        }}
      />
      {/* Overlay de chargement - Cible uniquement la grille du planning */}
      {isLoading && (
        <>
          <style>
            {`
              .sx-react-calendar-wrapper.planning-loading-blur .sx__time-grid-scroll-wrapper::before {
                content: '';
                position: absolute;
                top: 0;
                left: 0;
                right: 0;
                bottom: 0;
                backdrop-filter: blur(8px);
                -webkit-backdrop-filter: blur(8px);
                background-color: rgba(255, 255, 255, 0.3);
                pointer-events: none;
                z-index: 10;
              }
            `}
          </style>
          <div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center">
            <div className="pointer-events-auto relative z-10 rounded-lg bg-white/90 px-4 py-2 text-gray-500 shadow-sm">Chargement du planning...</div>
          </div>
        </>
      )}
      {/* Message d'overlay quand aucune période n'est configurée */}
      {!isLoading && (!planningData?.periods || planningData.periods.length === 0) && (
        <>
          {/* Overlay de flou ciblant uniquement la grille des événements */}
          <style>
            {`
              .planning-overlay-blur::before {
                content: '';
                position: absolute;
                top: 0;
                left: 0;
                right: 0;
                bottom: 0;
                backdrop-filter: blur(8px);
                -webkit-backdrop-filter: blur(8px);
                pointer-events: none;
                z-index: 1;
                border-radius: 0.5rem;
              }

              /* Cibler uniquement la zone scrollable de la grille (événements) */
              .sx-react-calendar-wrapper .sx__time-grid-scroll-wrapper {
                position: relative;
              }

              .sx-react-calendar-wrapper.planning-overlay-blur .sx__time-grid-scroll-wrapper::before {
                content: '';
                position: absolute;
                top: 0;
                left: 0;
                right: 0;
                bottom: 0;
                backdrop-filter: blur(8px);
                -webkit-backdrop-filter: blur(8px);
                background-color: rgba(255, 255, 255, 0.3);
                pointer-events: none;
                z-index: 10;
              }
            `}
          </style>
          <div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center">
            <div className="pointer-events-auto relative z-10 rounded-lg bg-white/95 p-6 text-center shadow-md">
              <div className="mb-2 text-lg text-gray-500">Aucune période configurée</div>
              <div className="text-sm text-gray-400">Configurez les horaires d'ouverture de votre club pour afficher le planning</div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export function ClubPlanningPage() {
  const { anchorDate, weekDays, goToNextWeek, goToPreviousWeek, goToToday, isCurrentWeek } = useWeekAnchor();
  const [selectedWeekType, setSelectedWeekType] = useState("semaine");

  // Charger le zoom depuis le localStorage, ou utiliser 2 (100%) par défaut
  const [zoomLevel, setZoomLevel] = useState(() => {
    const saved = localStorage.getItem("planningZoomLevel");
    return saved ? Number(saved) : 2;
  });

  // Sauvegarder le zoom dans le localStorage quand il change
  const handleZoomChange = (newZoom: number) => {
    setZoomLevel(newZoom);
    localStorage.setItem("planningZoomLevel", String(newZoom));
  };

  // État pour la sheet d'appel
  const [attendanceSheetOpen, setAttendanceSheetOpen] = useState(false);
  const [selectedPeriodData, setSelectedPeriodData] = useState<PeriodData | null>(null);
  // Rien ici: la gestion des overrides est déléguée au hook usePlanningCountsSource

  // Function to handle opening attendance sheet
  const handleOpenAttendance = useCallback((periodData: PeriodData) => {
    setSelectedPeriodData(periodData);
    setAttendanceSheetOpen(true);
  }, []);

  // Calculer le lundi de la semaine pour l'API
  const weekStart = formatLocalDate(anchorDate);

  // Appel API pour récupérer les données du planning
  const {
    data: planningData,
    error,
    isLoading,
  } = api.useQuery("get", "/planning/week", {
    params: {
      query: { weekStart },
    },
    // Options pour forcer le chargement immédiat
    staleTime: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    enabled: true, // S'assurer que la query est activée
  });

  // Calculer les bornes dynamiques pour la semaine courante (sans console.log initial)
  const dayBoundaries = useMemo(() => calculateDayBoundaries(planningData?.periods || [], weekDays), [planningData?.periods, weekDays]);

  // Calculer la hauteur de la grille en fonction du zoom
  const gridHeight = useMemo(() => computeGridHeight(dayBoundaries, zoomLevel), [dayBoundaries, zoomLevel]);

  // Events utilisés comme source de vérité pour les compteurs
  const eventsForCounts = useMemo(() => {
    if (!planningData) return [] as TimeGridEventData[];
    return generateCalendarEvents(planningData.periods, planningData.reservations, weekDays);
  }, [planningData, weekDays]);

  const { countsMap, setOptimisticCount } = usePlanningCountsSource(eventsForCounts);

  // Gestion des états d'erreur et de chargement
  if (error) {
    return (
      <div className="space-y-0 rounded-lg border border-gray-300 bg-white p-4">
        <div className="text-center text-red-600">Erreur lors du chargement du planning: {error.message}</div>
      </div>
    );
  }

  return (
    <PlanningCountsProvider value={countsMap}>
      <div className="flex h-[80vh] min-h-0 flex-col overflow-y-hidden rounded-lg border border-gray-300 bg-white">
        {/* Navigation du planning */}
        <CalendarHeader
          className="p-4"
          currentWeekStart={anchorDate}
          selectedWeekType={selectedWeekType}
          onPreviousWeek={goToPreviousWeek}
          onNextWeek={goToNextWeek}
          onToday={goToToday}
          onWeekTypeChange={setSelectedWeekType}
          isCurrentWeek={isCurrentWeek}
          zoomLevel={zoomLevel}
          onZoomChange={handleZoomChange}
        />

        {/* Calendrier Schedule-X */}
        <div className="min-h-0 flex-1 p-4 pt-0">
          <PlanningCalendar
            key={`${zoomLevel}-${dayBoundaries.start}-${dayBoundaries.end}`}
            anchorDate={anchorDate}
            weekDays={weekDays}
            dayBoundaries={dayBoundaries}
            gridHeight={gridHeight}
            planningData={planningData}
            handleOpenAttendance={handleOpenAttendance}
            isLoading={isLoading}
          />
        </div>

        {/* AttendanceSheet pour l'appel */}
        {selectedPeriodData && (
          <AttendanceSheet
            open={attendanceSheetOpen}
            onOpenChange={setAttendanceSheetOpen}
            periodTitle={selectedPeriodData.periodTitle}
            periodDate={selectedPeriodData.periodDate}
            periodTime={selectedPeriodData.periodTime}
            periodId={selectedPeriodData.periodId}
            date={selectedPeriodData.date}
            targetGroupId={selectedPeriodData.targetGroupId}
            onChildrenCountChange={({ periodId, date, count }) => {
              const eventId = `${periodId}-${date}`;
              setOptimisticCount(eventId, count);
            }}
          />
        )}
      </div>
    </PlanningCountsProvider>
  );
}
